/**
 * Media Attachment Picker Component
 * Allows users to select media assets to attach to messages/campaigns.
 * Supports multi-select, language selection, and link generation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Search,
  File,
  Image,
  Video,
  Music,
  Link as LinkIcon,
  Check,
  Plus,
  Upload,
  Globe,
  Languages,
  Copy,
  ExternalLink,
  RefreshCw,
  Filter
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

// Types
interface MediaAsset {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  asset_type: string;
  mime_type?: string;
  file_size?: number;
  thumbnail_url?: string;
  default_language: string;
  tags: string[];
  created_at: string;
  translation_count: number;
}

export interface SelectedAttachment {
  asset: MediaAsset;
  language: string;
  attachmentType: 'inline' | 'attachment' | 'link';
  shortUrl?: string;
}

interface MediaAttachmentPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (attachments: SelectedAttachment[]) => void;
  multiple?: boolean;
  allowedTypes?: string[];
  initialAttachments?: SelectedAttachment[];
  campaignId?: number;
}

const ASSET_TYPE_ICONS: Record<string, React.ElementType> = {
  document: File,
  image: Image,
  video: Video,
  audio: Music,
  web_link: LinkIcon,
};

const ASSET_TYPE_COLORS: Record<string, string> = {
  document: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
  image: 'text-green-500 bg-green-100 dark:bg-green-900/30',
  video: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
  audio: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
  web_link: 'text-cyan-500 bg-cyan-100 dark:bg-cyan-900/30',
};

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'ru', name: 'Russian' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'tl', name: 'Tagalog' },
];

export function MediaAttachmentPicker({
  isOpen,
  onClose,
  onSelect,
  multiple = true,
  allowedTypes,
  initialAttachments = [],
  campaignId
}: MediaAttachmentPickerProps) {
  // State
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<Map<number, SelectedAttachment>>(
    new Map(initialAttachments.map(a => [a.asset.id, a]))
  );
  const [showUpload, setShowUpload] = useState(false);

  // Fetch assets
  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedType) params.append('asset_type', selectedType);
      if (allowedTypes?.length) {
        // Filter by allowed types if specified
      }
      params.append('limit', '50');

      const res = await authFetch(`${getWeaveinApiUrl()}/api/media/assets?${params.toString()}`);

      if (res.ok) {
        const data = await res.json();
        setAssets(data.assets);
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedType, allowedTypes]);

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
    }
  }, [isOpen, fetchAssets]);

  // Toggle asset selection
  const toggleAsset = (asset: MediaAsset) => {
    const newSelected = new Map(selectedAssets);

    if (newSelected.has(asset.id)) {
      newSelected.delete(asset.id);
    } else {
      if (!multiple) {
        newSelected.clear();
      }
      newSelected.set(asset.id, {
        asset,
        language: asset.default_language,
        attachmentType: 'link'
      });
    }

    setSelectedAssets(newSelected);
  };

  // Update attachment options
  const updateAttachment = (assetId: number, updates: Partial<SelectedAttachment>) => {
    const newSelected = new Map(selectedAssets);
    const current = newSelected.get(assetId);
    if (current) {
      newSelected.set(assetId, { ...current, ...updates });
      setSelectedAssets(newSelected);
    }
  };

  // Generate short URLs for selected assets
  const generateShortUrls = async () => {

    for (const [assetId, attachment] of selectedAssets) {
      if (!attachment.shortUrl) {
        try {
          const res = await authFetch(`${getWeaveinApiUrl()}/api/media/links`, {
            method: 'POST',
            jsonBody: {
              asset_id: assetId,
              link_type: 'preview',
              campaign_id: campaignId,
              preferred_language: attachment.language
            }
          });

          if (res.ok) {
            const link = await res.json();
            updateAttachment(assetId, { shortUrl: link.full_url });
          }
        } catch (err) {
          console.error('Error creating short URL:', err);
        }
      }
    }
  };

  // Handle confirm
  const handleConfirm = async () => {
    // Generate short URLs for link-type attachments
    await generateShortUrls();

    const attachments = Array.from(selectedAssets.values());
    onSelect(attachments);
    onClose();
  };

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Select Media Attachments
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {selectedAssets.size} item{selectedAssets.size !== 1 ? 's' : ''} selected
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          <select
            value={selectedType || ''}
            onChange={(e) => setSelectedType(e.target.value || null)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">All Types</option>
            <option value="document">Documents</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="audio">Audio</option>
            <option value="web_link">Web Links</option>
          </select>

          <button
            onClick={() => setShowUpload(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>Upload</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Asset Grid */}
          <div className="flex-1 overflow-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            ) : assets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                <File className="w-12 h-12 mb-2 opacity-50" />
                <p>No assets found</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {assets.map((asset) => {
                  const TypeIcon = ASSET_TYPE_ICONS[asset.asset_type] || File;
                  const colorClass = ASSET_TYPE_COLORS[asset.asset_type] || '';
                  const isSelected = selectedAssets.has(asset.id);

                  return (
                    <div
                      key={asset.id}
                      onClick={() => toggleAsset(asset)}
                      className={`relative rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}

                      {/* Thumbnail */}
                      <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-t-lg flex items-center justify-center">
                        {asset.thumbnail_url ? (
                          <img
                            src={asset.thumbnail_url}
                            alt={asset.name}
                            className="w-full h-full object-cover rounded-t-lg"
                          />
                        ) : (
                          <div className={`p-3 rounded-full ${colorClass}`}>
                            <TypeIcon className="w-6 h-6" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {asset.name}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(asset.file_size)}
                          </span>
                          {asset.translation_count > 0 && (
                            <span className="flex items-center text-xs text-blue-500">
                              <Languages className="w-3 h-3 mr-1" />
                              {asset.translation_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Items Panel */}
          {selectedAssets.size > 0 && (
            <div className="w-72 border-l border-gray-200 dark:border-gray-700 overflow-auto">
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-white">Selected Items</h3>
              </div>

              <div className="p-3 space-y-3">
                {Array.from(selectedAssets.values()).map((attachment) => (
                  <div
                    key={attachment.asset.id}
                    className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                        {attachment.asset.name}
                      </p>
                      <button
                        onClick={() => toggleAsset(attachment.asset)}
                        className="text-gray-400 hover:text-red-500 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Language selector */}
                    <div className="mb-2">
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                        Language
                      </label>
                      <select
                        value={attachment.language}
                        onChange={(e) => updateAttachment(attachment.asset.id, { language: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {LANGUAGES.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Attachment type */}
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                        Attach as
                      </label>
                      <div className="flex space-x-1">
                        {['link', 'attachment', 'inline'].map((type) => (
                          <button
                            key={type}
                            onClick={() => updateAttachment(attachment.asset.id, { attachmentType: type as any })}
                            className={`flex-1 px-2 py-1 text-xs rounded ${
                              attachment.attachmentType === type
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {type === 'link' && <LinkIcon className="w-3 h-3 inline mr-1" />}
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Show generated URL if available */}
                    {attachment.shortUrl && (
                      <div className="mt-2 flex items-center space-x-1">
                        <input
                          type="text"
                          value={attachment.shortUrl}
                          readOnly
                          className="flex-1 px-2 py-1 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded"
                        />
                        <button
                          onClick={() => navigator.clipboard.writeText(attachment.shortUrl!)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Trackable short URLs will be generated for link attachments
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedAssets.size === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Check className="w-4 h-4" />
              <span>Attach {selectedAssets.size > 0 ? `(${selectedAssets.size})` : ''}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline Attachment Display Component
 * Shows attached media items in a message/campaign editor.
 */
export function AttachedMediaDisplay({
  attachments,
  onRemove,
  onEdit
}: {
  attachments: SelectedAttachment[];
  onRemove?: (assetId: number) => void;
  onEdit?: (assetId: number) => void;
}) {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      {attachments.map((attachment) => {
        const TypeIcon = ASSET_TYPE_ICONS[attachment.asset.asset_type] || File;

        return (
          <div
            key={attachment.asset.id}
            className="flex items-center space-x-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <TypeIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-900 dark:text-white max-w-32 truncate">
              {attachment.asset.name}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
              {attachment.language.toUpperCase()}
            </span>
            {attachment.attachmentType === 'link' && (
              <LinkIcon className="w-3 h-3 text-blue-500" />
            )}
            {onRemove && (
              <button
                onClick={() => onRemove(attachment.asset.id)}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Media Picker Button Component
 * A simple button to open the media picker.
 */
export function MediaPickerButton({
  onSelect,
  multiple = true,
  allowedTypes,
  className = ''
}: {
  onSelect: (attachments: SelectedAttachment[]) => void;
  multiple?: boolean;
  allowedTypes?: string[];
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center space-x-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 ${className}`}
      >
        <Plus className="w-4 h-4" />
        <span>Add Media</span>
      </button>

      <MediaAttachmentPicker
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSelect={onSelect}
        multiple={multiple}
        allowedTypes={allowedTypes}
      />
    </>
  );
}

export default MediaAttachmentPicker;
